import { type ReactNode, type ElementType, type ComponentPropsWithoutRef } from "react";
import { styled } from "~/common/mui";
import { C_TERTIARY_CONTAINER, C_ON_TERTIARY_CONTAINER } from "~/common/components/icon/Colors";

export type TagVariant = "default" | "success" | "warn" | "primary" | "salesCampaign" | "backInStock" | "error";

interface TagProps<T extends ElementType = "span"> {
  variant?: TagVariant;
  children: ReactNode;
  className?: string;
  as?: T;
  icon?: ReactNode;
  iconPosition?: "start" | "end";
}

const StyledTag = styled("span", {
  shouldForwardProp: (prop) => prop !== "variant",
})<{ variant: TagVariant }>(({ theme, variant }) => ({
  gap: theme.spacing(1),
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  borderRadius: "16px",
  ...theme.typography.labelMedium,
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  // Variant styles
  ...(variant === "default" && {
    backgroundColor: theme.palette.surface.dim,
    color: theme.palette.text.primary,
  }),
  ...(variant === "success" && {
    backgroundColor: theme.palette.success.container,
    color: theme.palette.success.onContainer,
  }),
  ...(variant === "warn" && {
    backgroundColor: theme.palette.warning.container,
    color: theme.palette.warning.onContainer,
  }),
  ...(variant === "error" && {
    backgroundColor: theme.palette.error.container,
    color: theme.palette.error.onContainer,
  }),
  ...(variant === "primary" && {
    backgroundColor: theme.palette.primary.container,
    color: theme.palette.primary.onContainer,
  }),
  ...(variant === "salesCampaign" && {
    backgroundColor: theme.palette.salesCampaign.main,
    color: theme.palette.salesCampaign.contrastText,
  }),
  ...(variant === "backInStock" && {
    backgroundColor: C_TERTIARY_CONTAINER,
    color: C_ON_TERTIARY_CONTAINER,
  }),
}));

const Tag = <T extends ElementType = "span">({
  children,
  variant = "default",
  as,
  className,
  icon,
  iconPosition = "end",
}: TagProps<T> & Omit<ComponentPropsWithoutRef<T>, keyof TagProps<T>>) => {
  const Component = StyledTag as unknown as ElementType;

  return (
    <Component as={as} className={className} variant={variant}>
      {iconPosition == "start" && icon}
      {children}
      {iconPosition == "end" && icon}
    </Component>
  );
};

export default Tag;
